import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FIRM, PENINSULA_ADDRESS, PENINSULA_OFFICE, faqSchema } from "@/lib/firm";
import {
  ESTATE_DISPUTE_LOCATIONS,
  getEstateDisputeLocation,
  type EstateDisputeLocation,
} from "@/lib/estate-dispute-locations";

const SITE = "https://coastsidelaw.com.au";

export const Route = createFileRoute("/estate-disputes/$suburb")({
  loader: ({ params }) => {
    const content = getEstateDisputeLocation(params.suburb);
    if (!content) throw notFound();
    return { content };
  },
  head: ({ params, loaderData }) => {
    const content = loaderData?.content;
    if (!content) {
      return { meta: [{ title: fitTitle("Not found") }, { name: "robots", content: "noindex" }] };
    }
    const url = `${SITE}/estate-disputes/${params.suburb}`;
    return {
      meta: [
        { title: fitTitle(content.metaTitle) },
        { name: "description", content: fitDescription(content.description) },
        { property: "og:title", content: fitTitle(content.metaTitle) },
        { property: "og:description", content: fitDescription(content.description) },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(faqSchema(content.faqs)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LegalService",
            name: `${FIRM.name} — estate dispute lawyers, ${content.name}`,
            description: content.description,
            telephone: FIRM.phone,
            address: {
              "@type": "PostalAddress",
              streetAddress: PENINSULA_OFFICE.street,
              addressLocality: PENINSULA_OFFICE.suburb,
              addressRegion: PENINSULA_OFFICE.state,
              postalCode: PENINSULA_OFFICE.postcode,
              addressCountry: "AU",
            },
            areaServed: [content.name, ...content.nearby].map((n) => ({
              "@type": "Place",
              name: `${n}, Queensland`,
            })),
            serviceType: [
              "Family provision claim",
              "Contesting a will",
              "Will validity challenge",
              "Executor and estate administration disputes",
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              {
                "@type": "ListItem",
                position: 2,
                name: "Estate disputes",
                item: `${SITE}/estate-disputes`,
              },
              { "@type": "ListItem", position: 3, name: content.name, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: EstateDisputeLocationPage,
});

const GUIDE_LINKS = [
  { slug: "contesting-a-will-in-queensland", t: "Contesting a will in Queensland: who can, and when" },
  { slug: "time-limits-contesting-a-will-queensland", t: "Time limits for contesting a will" },
  {
    slug: "parent-left-everything-to-one-child-queensland",
    t: "My parent left everything to one child",
  },
  {
    slug: "challenging-a-will-capacity-undue-influence-queensland",
    t: "Challenging a will: capacity and undue influence",
  },
  {
    slug: "how-much-does-it-cost-to-contest-a-will-queensland",
    t: "How much does it cost to contest a will?",
  },
  { slug: "estate-contested-executor-guide-queensland", t: "Executor's guide to a contested estate" },
] as const;

function EstateDisputeLocationPage() {
  const { content } = Route.useLoaderData() as { content: EstateDisputeLocation };

  return (
    <>
      <PageHero
        eyebrow={`Estate disputes — ${content.name}`}
        title={content.title}
        lead={content.lead}
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="rounded-sm border-l-4 border-accent bg-secondary/60 p-6">
          <h2 className="font-serif text-xl text-foreground">Two deadlines run from the date of death</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Written notice to the executor within <strong>six months</strong>. Court application
            filed within <strong>nine months</strong>. Neither clock waits for probate. If you are
            close to either date, call {FIRM.phone} today.
          </p>
        </div>

        {content.paragraphs.map((p) => (
          <p key={p} className="mt-6 text-base leading-relaxed text-muted-foreground">
            {p}
          </p>
        ))}

        <h2 className="mt-14 font-serif text-3xl text-foreground">
          What we see in {content.name} estates
        </h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          {content.localNotes.map((n) => (
            <div key={n.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg text-foreground">{n.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{n.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Where we meet you</h2>
        <p className="mt-3 text-base leading-relaxed text-muted-foreground">
          Our peninsula office is at {PENINSULA_ADDRESS}, by appointment, alongside our Sunshine
          Coast office at {FIRM.street}, {FIRM.suburb}. We act for families across {content.name}{" "}
          and {content.nearby.join(", ")}.
        </p>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who will act for you" />

        <h2 className="mt-14 font-serif text-3xl text-foreground">Read these first</h2>
        <ul className="mt-5 space-y-2 text-sm">
          {GUIDE_LINKS.map((g) => (
            <li key={g.slug}>
              <Link
                to="/guides/$slug"
                params={{ slug: g.slug }}
                className="font-semibold text-primary underline"
              >
                {g.t}
              </Link>
            </li>
          ))}
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Other areas we act in</h2>
        <ul className="mt-5 flex flex-wrap gap-2">
          {ESTATE_DISPUTE_LOCATIONS.filter((l) => l.slug !== content.slug).map((l) => (
            <li key={l.slug}>
              <Link
                to="/estate-disputes/$suburb"
                params={{ suburb: l.slug }}
                className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
              >
                Contesting a will {l.name}
              </Link>
            </li>
          ))}
          <li>
            <Link
              to="/moreton-bay/$suburb"
              params={{ suburb: "redcliffe" }}
              className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
            >
              Moreton Bay conveyancing &amp; wills
            </Link>
          </li>
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">
          {content.name} estate dispute FAQs
        </h2>
        <div className="mt-6">
          <FaqList items={content.faqs} />
        </div>

        <div className="mt-14">
          <EnquiryForm heading={`Talk to us about a ${content.name} estate`} />
        </div>
        <div className="mt-14">
          <ExecutorIntakeForm heading="Estate or executor enquiry" />
        </div>
      </section>

      <CtaBand
        title="Close to the six or nine month deadline?"
        body={`Call ${FIRM.phone}. A short conversation now can protect your position while we look at the will properly.`}
      />
      <div className="h-20" />
    </>
  );
}
